import type { ComparisonRow, ComparisonExtraColumn } from "../types";

export default function CityComparisonSection({
  city,
  regionalAgency,
  rows,
  extraColumns,
}: {
  city: string;
  regionalAgency: { name: string; address: string; distance: string };
  rows: ComparisonRow[];
  extraColumns?: ComparisonExtraColumn[];
}) {
  const hasExtras = extraColumns && extraColumns.length > 0;
  const totalCols = 3 + (hasExtras ? extraColumns.length : 0);
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Emergency Passports</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            {hasExtras ? `${city} Passport Service Comparison` : "Regional Passport Agency vs. Fast Passport Center"}
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base">
            {hasExtras
              ? `We've compared the top passport service options for ${city} area travelers — from courier service to post office to third-party resellers.`
              : <>If you need your passport <strong className="text-[#1A365D]">within two weeks</strong>, the {regionalAgency.name} is your only do-it-yourself option — {regionalAgency.distance}. Getting an appointment there is rarely straightforward. Fast Passport Center gives you a faster, simpler path without the uncertainty.</>}
          </p>
        </div>

        {/* Agency Appointment Not Available Callout */}
        <div className="mb-8 bg-amber-50 border border-[#F6C344]/40 rounded-lg p-6">
          <h3 className="font-serif text-lg font-bold text-[#1A365D] mb-3">
            {city} Passport Agency Appointment Not Available?
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Regional passport agency appointments{regionalAgency.address.toLowerCase().includes(city.toLowerCase()) ? ` in ${city}` : ""} are routinely booked 2–4 weeks out — and walk-ins are never accepted. If you searched for an appointment and came up empty, Fast Passport Center's registered courier channel is your fastest next step. No appointment. No waiting in line at the federal building. Processing as fast as 2–3 business days.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all hover:scale-105 whitespace-nowrap"
          >
            Get Started <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {hasExtras ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                  <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 bg-[#1A365D]/90">
                    <i className="ri-shield-check-line mr-1.5 text-[#F6C344]"></i>Fast Passport Center
                  </th>
                  {extraColumns.map((col, ci) => (
                    <th key={ci} className={`bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 ${ci === extraColumns.length - 1 ? "rounded-tr-lg" : ""}`}>
                      {col.icon ? <i className={`${col.icon} mr-1.5`}></i> : null}{col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                    <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                    <td className="px-4 py-3.5 text-sm text-green-600 font-medium bg-[#F6C344]/5">{row.fpc}</td>
                    {extraColumns.map((col, ci) => (
                      <td key={ci} className="px-4 py-3.5 text-sm text-gray-500">{col.rows[i]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <div className="grid grid-cols-3 bg-[#1A365D] text-white">
                <div className="p-4 text-sm font-semibold text-white/50">Feature</div>
                <div className="p-4 text-sm font-semibold text-center border-l border-white/10">
                  <i className="ri-government-line mr-1.5"></i>Regional Passport Agency
                </div>
                <div className="p-4 text-sm font-semibold text-center border-l border-white/10 bg-[#F6C344]/15">
                  <i className="ri-shield-check-line mr-1.5 text-[#F6C344]"></i>Fast Passport Center
                </div>
              </div>

              {rows.map((row, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}`}
                >
                  <div className="p-4 text-sm font-semibold text-[#1A365D]">{row.feature}</div>
                  <div className="p-4 text-sm text-gray-500 border-l border-gray-100 flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-400 flex-shrink-0 mt-0.5"></i>
                    <span>{row.agency}</span>
                  </div>
                  <div className="p-4 text-sm text-gray-700 border-l border-gray-100 flex items-start gap-2 bg-[#F6C344]/5">
                    <i className="ri-checkbox-circle-line text-[#F6C344] flex-shrink-0 mt-0.5"></i>
                    <span className="font-medium">{row.fpc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <a
                href="https://fastpassportcenter.com/checkout/"
                className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
              >
                Start My Expedited Passport <i className="ri-arrow-right-line"></i>
              </a>
              <p className="text-gray-400 text-xs mt-3">
                {regionalAgency.name} — {regionalAgency.address} — {regionalAgency.distance}
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}